import { DECIMAL_PATTERN } from '../../api';

/**
 * A non-negative decimal held exactly: `value = units / 10^scale`. Lots, risk percents and pips
 * never go through a binary float (standards §1), so "0.07" stays 7 hundredths.
 */
export interface ExactDecimal {
  readonly units: bigint;
  readonly scale: number;
}

const INPUT = /^(\d+)(?:[.,](\d+))?$/;

/**
 * Reads what the trader typed. A comma is accepted as the decimal separator (French keyboards);
 * nothing else is changed: no rounding, no clamping (I7, FXM-02 §7 "no size adjusted silently").
 *
 * @returns the exact value and its wire string (C1 `DecimalStringSchema`), or `null` when the
 *   text is not a plain non-negative decimal or would not fit the contract pattern.
 * @example readDecimal('0,5') // { value: { units: 5n, scale: 1 }, wire: '0.5' }
 */
export function readDecimal(text: string): { value: ExactDecimal; wire: string } | null {
  const match = INPUT.exec(text.trim());
  if (match === null) return null;
  const [, integerPart = '', fraction = ''] = match;
  // "007" becomes "7": the same number, written the way the contract accepts it.
  const integer = BigInt(integerPart).toString();
  const wire = fraction === '' ? integer : `${integer}.${fraction}`;
  if (!DECIMAL_PATTERN.test(wire)) return null;
  return { value: { units: BigInt(integer + fraction), scale: fraction.length }, wire };
}

const HALF_TYPED = /^(?:\d+[.,]|[.,]\d*)$/;

/**
 * True for a number the trader is still typing: a separator with digits on one side only
 * ("0,", "1.", ",5", "."). The form treats it as incomplete, not wrong, so the field does not
 * turn red between two keystrokes; Save stays disabled all the same, and {@link readDecimal}
 * still refuses it (it is never completed to "0.5" or "1" for the trader, I7).
 */
export function isHalfTypedDecimal(text: string): boolean {
  return HALF_TYPED.test(text.trim());
}

/**
 * True for a number typed with no digit before its separator (",5", ".5"). Still half-typed
 * ({@link isHalfTypedDecimal}): it is never read as "0.5" for the trader (I7), but the form says
 * what is missing, since a trader may well stop there (M2 review, proposal, to be validated).
 */
export function lacksLeadingDigit(text: string): boolean {
  return /^[.,]\d+$/.test(text.trim());
}

/** Parses a contract decimal string (already valid on the wire). */
export function parseWireDecimal(wire: string): ExactDecimal {
  const read = readDecimal(wire);
  if (read === null) throw new RangeError(`Not a decimal string: ${wire}`);
  return read.value;
}

/** Compares two exact decimals: negative, zero or positive like `Array.prototype.sort`. */
export function compareDecimal(a: ExactDecimal, b: ExactDecimal): number {
  const scale = Math.max(a.scale, b.scale);
  const left = a.units * 10n ** BigInt(scale - a.scale);
  const right = b.units * 10n ** BigInt(scale - b.scale);
  return left === right ? 0 : left < right ? -1 : 1;
}

/** Exact integer value of `whole`, as a decimal. */
export function wholeDecimal(whole: number): ExactDecimal {
  return { units: BigInt(whole), scale: 0 };
}
